import { useId, useLayoutEffect, useRef, useState } from 'react'
import { cn } from '../../lib/cn'
import type { HoleGeometry, HoleScore, LatLng, Shot } from '../../lib/golfRound'
import {
  holeTrace,
  pathFrom,
  polylinePoints,
  projectHole,
  resolveTraceFinish,
  shotsForHole,
  type HoleTrace,
  type PlanPoint,
} from '../../lib/holeProjection'
import { traceColor, traceDash, traceInitial, traceStyleFor } from './trace'
import {
  LEG_PILL_HEIGHT,
  holePlanFurnitureScale,
  legPillLayout,
  pillPlacementFor,
} from './legPill'

/** One player's card, as far as a drawing is concerned. */
export interface HolePlanPlayer {
  id: string
  /** `round_players.position`, 1-4 — what the tone and dash come off. */
  position: number
  displayName: string
  /**
   * The player's shots. The round's flat, all-holes array is fine: the plan
   * filters and orders it itself (`shotsForHole`), because that array is flat
   * by design and every drawing pays the same toll.
   */
  shots: readonly Shot[]
  /** The whole score classifies a recorded, mapped-center or partial finish. */
  scores?: readonly HoleScore[]
}

export interface HolePlanProps {
  hole: HoleGeometry
  /** The COURSE's scale, from the geometry payload. Never derived locally. */
  scale: [number, number]
  /** The hole's par, for the accessible title. Null when the card is unseeded. */
  par?: number | null
  players?: readonly HolePlanPlayer[]
  /**
   * The one player whose leg YARDAGES are printed.
   *
   * Four players' yardages on one hole is 12-16 numbers over each other and
   * legible as none of them, so the numbers appear only when a screen has
   * narrowed to a single player. The traces themselves always draw — telling
   * the fourball apart is what the tone, dash and initial are for.
   */
  focusedPlayerId?: string | null
  width?: number
  height?: number
  className?: string
}

/** A portrait plate, because the rotation puts every hole up the long axis. */
const DEFAULT_WIDTH = 300
const DEFAULT_HEIGHT = 420

/** A 20-unit strike dot: 16 across inside a 2-unit parchment ring. */
const MARK_RADIUS = 9
const MARK_STROKE_WIDTH = 2

/**
 * The pin, at `EmptyPlate.tsx:36-39`'s proportions.
 *
 * Reused rather than redrawn: the club has ONE pin-and-flag and it is the mark
 * a member already reads as "the hole" on every empty plate in the clubhouse.
 * Those numbers live in a 70x62 box with the pin standing on y=52; re-expressed
 * here relative to its own ground point, the pole is 40 units tall, the finial
 * is a 2.6 circle a unit above it, and the flag flies from (0,-37) to (22,-31)
 * to (0,-24). `PIN_HEIGHT` is the only thing that changes — the whole mark is
 * scaled by it, so the proportions cannot drift from the plate's.
 */
const PIN_UNITS_TALL = 40
const PIN_HEIGHT = 26

/** The tee, as a small filled square — the club's scorecard mark for a teeing ground. */
const TEE_SIDE = 7

/**
 * A hazard OSM gave us as a single NODE has no extent, so what is drawn is a
 * MARKER at a nominal size rather than a measurement of anything. Six metres is
 * a plausible small bunker and is deliberately not tuned to look like a
 * particular hazard: nobody should read a traced polygon and a node blob as
 * making the same claim.
 */
const NODE_HAZARD_METRES = 6

/**
 * The engraved plan of one hole, with the shots played on it.
 *
 * ## What this draws, in order, and why that order
 *
 * outline → fairways → hazards → green → centre line → tee and pin → traces →
 * marks and yardages. It is back to front, the way an engraving is built up:
 * ground first, then the features cut into it, then the furniture, and the
 * member's own round last so nothing the club drew can sit on top of what the
 * member did.
 *
 * ## The degradation matrix (test-pinned, `__tests__/holePlan.test.tsx`)
 *
 * | State | What renders |
 * |---|---|
 * | geometry + 2 or more shots | the plan, a polyline, a mark per strike point |
 * | geometry + exactly 1 shot, no positive-putt finish | the plan and ONE mark — no line, because a leg needs two known points |
 * | geometry + 0 shots | the plan alone |
 * | NO geometry | **nothing — this component is not rendered at all** |
 *
 * That last row is the caller's, deliberately: 7 of the club's 15 courses are
 * unmapped, and an empty frame would still spend the height and still announce
 * itself to a screen reader. `hasGeometry(geometry, holeNumber)` in
 * `lib/holeProjection.ts` is the guard, and the honest line beside it is the
 * screen's to write.
 *
 * ## Two implementation rules that fail silently when broken
 *
 * **Every `defs` id is minted from `useId()`.** Crest's docstring records what
 * the alternative costs: two instances sharing an id both resolve to whichever
 * mounted first, and the second's referenced content silently vanishes. A
 * round page draws up to eighteen of these plans, so a literal id would take
 * out seventeen of them.
 *
 * **Colours are CSS custom properties, never hex.** The palette is authored
 * once in `entrypoints/fireside.css`; a hex table in TypeScript drifts the day
 * a token is corrected on the design card.
 */
export function HolePlan({
  hole,
  scale,
  par = null,
  players = [],
  focusedPlayerId = null,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  className,
}: HolePlanProps) {
  const svg = useRef<SVGSVGElement>(null)
  const [renderedWidth, setRenderedWidth] = useState(width)
  const furnitureScale = holePlanFurnitureScale(width, renderedWidth)
  const uid = useId()
  const titleId = `hole-plan-title-${uid}`
  const descriptionId = `hole-plan-desc-${uid}`
  const hatchId = `hole-plan-hatch-${uid}`

  const walks = players.map((player) => ({
    player,
    shots: shotsForHole(player.shots, hole.number),
    score: player.scores?.find((score) => score.hole_number === hole.number),
  }))

  // Framed on every strike point of the hole, not only the drawn prefix, plus
  // each applicable finish endpoint. Either can finish outside every polygon
  // the course file has, and an SVG root CLIPS — so a known point left out of
  // the fit is a point silently not drawn.
  const strikePoints: LatLng[] = walks.flatMap((walk) =>
    walk.shots.map((shot): LatLng => [shot.lat, shot.lng]),
  )
  const finishPoints: LatLng[] = walks.flatMap((walk) => {
    const endpoint = resolveTraceFinish(walk.shots, hole, walk.score).endpoint
    return endpoint === null ? [] : [endpoint]
  })
  const projection = projectHole(hole, scale, { width, height, padding: 44 * furnitureScale }, [
    ...strikePoints,
    ...finishPoints,
  ])
  const traces = walks.map((walk) => ({
    player: walk.player,
    trace: holeTrace(walk.shots, hole, scale, projection, walk.score),
  }))

  const drawn = traces.filter((entry) => entry.trace.marks.length > 0)
  const focused = focusedPlayerId === null ? null : drawn.find((entry) => entry.player.id === focusedPlayerId)

  const hazards = hole.hazards ?? []
  const water = hazards.filter((hazard) => hazard.kind.includes('water'))
  const bunkers = hazards.filter((hazard) => hazard.kind === 'bunker')
  const nodeRadius = Math.max(2, NODE_HAZARD_METRES / projection.metresPerUnit)

  const project = (point: LatLng) => projection.toPoint(point)
  const ring = (points: LatLng[]) => pathFrom(points.map(project), { close: true })

  useLayoutEffect(() => {
    const element = svg.current
    if (element === null) return

    const measure = (next: number) => {
      if (Number.isFinite(next) && next > 0) setRenderedWidth((current) => (current === next ? current : next))
    }
    measure(element.getBoundingClientRect().width)
    if (typeof ResizeObserver !== 'function') return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) measure(entry.contentRect.width)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [width])

  return (
    <svg
      ref={svg}
      viewBox={projection.viewBox}
      // NEVER `none` and never `slice`: `none` would stretch the hole to the
      // caller's box and put a yardage's drawn length at the mercy of a
      // container width, and `slice` would crop the very ends of the hole the
      // fit exists to include.
      preserveAspectRatio="xMidYMid meet"
      role="img"
      // The SVG accessibility mapping would take `<title>` as the name on its
      // own, but browsers and testing tools disagree about how far they
      // implement it — pointing at the ids is the shape every one of them
      // computes the same way, and it keeps name and DESCRIPTION separate
      // rather than reading the whole round out as the picture's name.
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      className={cn('block h-auto w-full', className)}
      data-hole-plan={hole.number}
    >
      <title id={titleId}>{titleFor(hole.number, par)}</title>
      <desc id={descriptionId}>{descriptionFor(drawn)}</desc>

      {water.length > 0 && (
        <defs>
          {/* Hatching, not a flat blue: the club's palette has no water colour
              and inventing one would put a fifteenth token on the page. A
              45° rule in fairway green reads as water on cream paper the way
              a printed plan does. */}
          <pattern id={hatchId} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" fill="var(--color-sage-soft)" />
            <line x1="0" y1="0" x2="0" y2="6" stroke="var(--color-fairway)" strokeWidth="1" opacity="0.55" />
          </pattern>
        </defs>
      )}

      {/* 1 — the hole's own ground. */}
      {hole.outline && hole.outline.length >= 3 && (
        <path
          d={ring(hole.outline)}
          fill="var(--color-paper-deep)"
          stroke="var(--color-brass)"
          strokeWidth="1"
          strokeOpacity="0.45"
          vectorEffect="non-scaling-stroke"
          data-layer="outline"
        />
      )}

      {/* 2 — fairways. Plural: a split fairway is one hole with two of them. */}
      {(hole.fairways ?? []).map((fairway, index) =>
        fairway.length >= 3 ? (
          <path
            key={`fairway-${index}`}
            d={ring(fairway)}
            fill="var(--color-sage-soft)"
            stroke="none"
            data-layer="fairway"
          />
        ) : null,
      )}

      {/* 3 — hazards, under the green so a greenside bunker reads as beside it. */}
      {water.map((hazard, index) =>
        hazard.polygon && hazard.polygon.length >= 3 ? (
          <path
            key={`water-${index}`}
            d={ring(hazard.polygon)}
            fill={`url(#${hatchId})`}
            stroke="var(--color-fairway)"
            strokeWidth="1"
            strokeOpacity="0.5"
            vectorEffect="non-scaling-stroke"
            data-layer="water"
          />
        ) : (
          <circle
            key={`water-${index}`}
            cx={project(hazard.point).x}
            cy={project(hazard.point).y}
            r={nodeRadius}
            fill={`url(#${hatchId})`}
            data-layer="water"
          />
        ),
      )}
      {bunkers.map((hazard, index) =>
        hazard.polygon && hazard.polygon.length >= 3 ? (
          <path
            key={`bunker-${index}`}
            d={ring(hazard.polygon)}
            fill="var(--color-gilt)"
            fillOpacity="0.55"
            stroke="var(--color-brass)"
            strokeWidth="1"
            strokeOpacity="0.5"
            vectorEffect="non-scaling-stroke"
            data-layer="bunker"
          />
        ) : (
          <circle
            key={`bunker-${index}`}
            cx={project(hazard.point).x}
            cy={project(hazard.point).y}
            r={nodeRadius}
            fill="var(--color-gilt)"
            fillOpacity="0.55"
            data-layer="bunker"
          />
        ),
      )}

      {/* 4 — the green. */}
      {hole.green.length >= 3 && (
        <path
          d={ring(hole.green)}
          fill="var(--color-sage)"
          fillOpacity="0.5"
          stroke="var(--color-fairway)"
          strokeWidth="1.2"
          vectorEffect="non-scaling-stroke"
          data-layer="green"
        />
      )}

      {/* 5 — the centre line, dashed. Brass is a RULE here, never a label. */}
      {hole.path.length >= 2 && (
        <polyline
          points={polylinePoints(hole.path.map(project))}
          fill="none"
          stroke="var(--color-brass)"
          strokeWidth="1"
          strokeDasharray="4 5"
          strokeOpacity="0.7"
          vectorEffect="non-scaling-stroke"
          data-layer="centre-line"
        />
      )}

      {/* 6 — the furniture: the teeing ground, and the club's own pin. */}
      <TeeMark point={project(hole.tee)} />
      <PinMark point={project(hole.pin)} />

      {/* 7 — the round. */}
      {traces.map((entry) => (
        <PlayerTrace
          key={entry.player.id}
          player={entry.player}
          trace={entry.trace}
          furnitureScale={furnitureScale}
        />
      ))}

      {/* 8 — the yardages, for one player at a time. */}
      {focused?.trace.legs.map((leg, index) => (
        <LegPill
          key={`leg-${index}`}
          leg={leg}
          plan={{ width, height }}
          furnitureScale={furnitureScale}
        />
      ))}
    </svg>
  )
}

function TeeMark({ point }: { point: PlanPoint }) {
  return (
    <rect
      x={point.x - TEE_SIDE / 2}
      y={point.y - TEE_SIDE / 2}
      width={TEE_SIDE}
      height={TEE_SIDE}
      fill="var(--color-pine)"
      data-layer="tee"
    />
  )
}

function PinMark({ point }: { point: PlanPoint }) {
  const k = PIN_HEIGHT / PIN_UNITS_TALL
  return (
    <g transform={`translate(${point.x} ${point.y}) scale(${k})`} data-layer="pin">
      <line x1="0" y1="0" x2="0" y2={-PIN_UNITS_TALL} stroke="var(--color-pine)" strokeWidth="1.8" />
      <circle cx="0" cy={-(PIN_UNITS_TALL + 1)} r="2.6" fill="var(--color-pine)" />
      <path d="M0 -37 L22 -31 L0 -24" fill="var(--color-flag)" opacity="0.9" />
    </g>
  )
}

function PlayerTrace({
  player,
  trace,
  furnitureScale,
}: {
  player: HolePlanPlayer
  trace: HoleTrace
  furnitureScale: number
}) {
  const color = traceColor(player.position)
  const dash = traceDash(player.position)
  const initial = traceInitial(player.displayName)
  const points = trace.polyline.length > 1 ? polylinePoints(trace.polyline) : undefined

  return (
    <g data-hole-plan-player={player.id}>
      {/* A single shot draws NO polyline: a leg needs two strike points, and a
          one-point line is a line to somewhere nobody has been. */}
      {trace.polyline.length > 1 && (
        <>
          <polyline
            points={points}
            fill="none"
            stroke="var(--color-parchment)"
            strokeWidth="4.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            // Contrast support only: it must not enlarge or replace the
            // player's original trace hit area.
            pointerEvents="none"
            data-trace-understroke={player.id}
          />
          <polyline
            points={points}
            fill="none"
            stroke={color}
            strokeWidth="1.8"
            strokeDasharray={dash}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            data-hole-trace={player.id}
            data-trace-pattern={traceStyleFor(player.position).label}
          />
        </>
      )}
      {trace.marks.map((mark) => (
        <g
          key={mark.sequence}
          transform={`translate(${mark.point.x} ${mark.point.y}) scale(${furnitureScale})`}
          data-strike-mark={mark.sequence}
          data-fixed-shot-furniture
          data-furniture-scale={furnitureScale}
        >
          <circle
            cx="0"
            cy="0"
            r={MARK_RADIUS}
            fill={color}
            stroke="var(--color-parchment)"
            strokeWidth={MARK_STROKE_WIDTH}
            vectorEffect="non-scaling-stroke"
          />
          {initial && (
            <text
              x="0"
              y="0"
              textAnchor="middle"
              dominantBaseline="central"
              fontFamily="var(--font-text)"
              fontSize="9"
              fontWeight="700"
              fill="var(--color-parchment)"
            >
              {initial}
            </text>
          )}
        </g>
      ))}
    </g>
  )
}

function LegPill({
  leg,
  plan,
  furnitureScale,
}: {
  leg: HoleTrace['legs'][number]
  plan: { width: number; height: number }
  furnitureScale: number
}) {
  const distance = `${leg.yards}y`
  const layout = legPillLayout(leg.club)
  const { clubWidth, distanceWidth, width } = layout
  const placement = pillPlacementFor(leg.from, plan, width)

  return (
    <g
      transform={`translate(${leg.from.x} ${leg.from.y}) scale(${furnitureScale})`}
      data-leg-pill
      data-leg-yardage={leg.yards}
      data-pill-width={width}
      data-fixed-shot-furniture
      data-furniture-scale={furnitureScale}
      aria-hidden="true"
    >
      <g transform={`translate(${placement.x} ${placement.y})`}>
        <rect
          width={width}
          height={LEG_PILL_HEIGHT}
          rx="2"
          fill="var(--color-ink)"
          fillOpacity="0.82"
          data-leg-pill-segment="distance"
        />
        {leg.club !== null && (
          <>
            <path
              d={`M2 0 H${clubWidth} V${LEG_PILL_HEIGHT} H2 Q0 ${LEG_PILL_HEIGHT} 0 ${LEG_PILL_HEIGHT - 2} V2 Q0 0 2 0 Z`}
              fill="var(--color-ink)"
              fillOpacity="0.94"
              data-leg-pill-segment="club"
            />
            <text
              x={clubWidth / 2}
              y={LEG_PILL_HEIGHT / 2}
              textAnchor="middle"
              dominantBaseline="central"
              fontFamily="var(--font-text)"
              fontSize="11"
              fontWeight="700"
              letterSpacing="0.4"
              textLength={layout.clubTextLength ?? undefined}
              lengthAdjust={layout.clubTextLength === null ? undefined : 'spacingAndGlyphs'}
              fill="var(--color-gilt)"
              data-leg-club
            >
              {layout.displayClub}
            </text>
          </>
        )}
        <text
          x={clubWidth + distanceWidth / 2}
          y={LEG_PILL_HEIGHT / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="var(--font-text)"
          fontSize="12.5"
          fontWeight="600"
          fill="var(--color-parchment)"
          data-leg-distance
        >
          {distance}
        </text>
      </g>
    </g>
  )
}

function titleFor(number: number, par: number | null): string {
  return par === null ? `Hole ${number}` : `Hole ${number}, par ${par}`
}

/**
 * What a screen reader is told about the round on this hole — the traces are
 * pure geometry and announce nothing on their own.
 *
 * Names recorded shots and measured legs, plus the explicit line-only
 * inference that positive putts can finish at the mapped green center. A hole
 * nobody took a shot on says so rather than describing an empty picture as a
 * round.
 */
function descriptionFor(drawn: { player: HolePlanPlayer; trace: HoleTrace }[]): string {
  if (drawn.length === 0) return 'No shots were recorded on this hole.'

  const lines = drawn.map((entry) => {
    const shots = entry.trace.marks.length
    const pattern = traceStyleFor(entry.player.position).label
    const legs = describeLegs(entry.trace.legs)
    const finish =
      entry.trace.finish === 'recorded'
        ? 'trace reaches the green'
        : entry.trace.finish === 'mapped-center'
          ? 'trace reaches the mapped green center'
          : 'tracking stops at the last recorded position'
    return `${entry.player.displayName}, ${pattern} line, ${shots} ${shots === 1 ? 'shot' : 'shots'} drawn${legs}; ${finish}`
  })
  return `Shot traces. ${lines.join('; ')}.`
}

function describeLegs(legs: HoleTrace['legs']): string {
  if (legs.length === 0) return ''
  const labels = legs.map((leg) => `${leg.club === null ? '' : `${leg.club} `}${leg.yards} yards`)
  const listed =
    labels.length === 1 ? labels[0]! : `${labels.slice(0, -1).join(', ')} and ${labels.at(-1)!}`

  return `, ${legs.length === 1 ? 'leg' : 'legs'} ${listed}`
}
